import { IRechnungsposition, IRechnungspositionenResponse } from '../../../models/ApiTypes';
import * as React from 'react';
import { observer } from 'mobx-react';
import { action, computed, observable } from 'mobx';
import { Col, ControlLabel, Table, Button, Form, FormControl, FormGroup, InputGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import Callout from '../../site/Callout';
import FaIcon from '../../site/FaIcon';
import { ApiStore } from '../../../stores/ApiStore';
import { bind, inject } from '../../../utils/decorators';
import { VorgangStore } from '../../../stores/VorgangStore';
import { AuftragStore } from '../../../stores/AuftragStore';
import Rechnungsposition from './Rechnungsposition';
import DienstleistungsUebersicht from './DienstleistungsUebersicht';

interface IDienstleistungsnachweisProps {
  vorgangId: string;
  onTriggerDocumentRefresh?: () => void;
}

@observer
export default class Dienstleistungsnachweis extends React.Component<IDienstleistungsnachweisProps, {}> {

  @inject(ApiStore) apiStore: ApiStore;
  @inject(VorgangStore) vorgangStore: VorgangStore;
  @inject(AuftragStore) auftragStore: AuftragStore;

  @observable dokumentStatus: 'nichtVorhanden' | 'angefordert' | 'erzeugt' = 'nichtVorhanden';
  @observable vorhandenePositionen: IRechnungspositionenResponse;
  @observable gewaehltePositionen: IRechnungspositionenResponse = [];
  @observable mailRechnung: string = '';
  @observable rechnungVersandArt: 'digital' | 'post' = 'digital';

  componentDidMount() {
    this.loadDienstleistungsnachweis();
    if (this.auftragStore.auftrag && this.auftragStore.auftrag.auftraggeber_email2) {
      this.setMailRechnung(this.auftragStore.auftrag.auftraggeber_email2);
    }
  }

  async loadDienstleistungsnachweis() {
    let dienstleistung: string = '';
    if (this.vorgangStore.vorgang) {
      dienstleistung = this.vorgangStore.vorgang.kuerzel_dienstleistung;
    }
    let rechnungspositionen = await this.apiStore.rechnungspositionenDienstleistung(dienstleistung);
    if (rechnungspositionen.ok) {
      this.setRechnungspositionen(rechnungspositionen.json);
    }
  }

  @bind @action setzeDokumentStatus(status: 'nichtVorhanden' | 'angefordert' | 'erzeugt') {
    this.dokumentStatus = status;
  }

  @bind @action setRechnungspositionen(rechnungspositionen: IRechnungspositionenResponse) {
    this.vorhandenePositionen = rechnungspositionen;
  }

  @bind @action setMailRechnung(mail: string) {
    this.mailRechnung = mail;
  }

  @bind @action handleMailRechnungChanged(event: React.FormEvent<FormControl>) {
    this.mailRechnung = (event.target as HTMLInputElement).value;
  }

  @bind @action handleMailRechnungAusAuftraggeber(event: React.FormEvent<FormControl>) {
    if (this.auftragStore.auftrag && this.auftragStore.auftrag.auftraggeber_email) {
      this.setMailRechnung(this.auftragStore.auftrag.auftraggeber_email);
    }
  }

  @bind @action handleRechnungVersandArtChanged(event: React.FormEvent<FormControl>) {
    if ((event.target as HTMLInputElement).value === 'digital') {
      this.rechnungVersandArt = 'digital';
    }
    if ((event.target as HTMLInputElement).value === 'post') {
      this.rechnungVersandArt = 'post';
    }
  }

  @computed get eingabenVollstaendig() {
    var vollstaendig = this.gewaehltePositionen.length > 0;
    if (this.rechnungVersandArt === 'digital' && this.mailRechnung.trim() === '') {
      vollstaendig = false;
    }
    return vollstaendig;
  }

  @bind async rechnungErzeugen() {
    this.setzeDokumentStatus('angefordert');

    // TODO: Anzahl direkt setzen
    // TODO: Unschöner Code, Kategorie + Sortierung u.U. nicht eindeutig
    this.vorhandenePositionen.forEach((position) => position.anzahl = this.gewaehltePositionen.filter((el) => el.kuerzel_dienstleistung === position.kuerzel_dienstleistung && el.sortierung === position.sortierung).length );

    const response = await this.apiStore.rechnungErzeugen(this.props.vorgangId, this.vorhandenePositionen, this.mailRechnung, this.rechnungVersandArt === 'post');
    if (response.ok) {
      if (this.props.onTriggerDocumentRefresh) {
        this.props.onTriggerDocumentRefresh();
      }
      this.vorgangStore.loadVorgang(this.props.vorgangId);
      this.setzeDokumentStatus('erzeugt');
    }
  }

  @bind @action setAnzahlHandler(anzahl: number, position: IRechnungsposition) {
    // Alle bestehenden Einträge der geänderten Position entfernen...
    this.gewaehltePositionen = this.gewaehltePositionen.filter((element) => element.bezeichnung !== position.bezeichnung);
    // und die neue Position so oft wie gefordert hinzufügen.
    let neuePositionen = [];
    for (let i = 0; i < anzahl; ++i) {
      neuePositionen.push(position);
    }
    this.gewaehltePositionen = this.gewaehltePositionen.concat(neuePositionen);
  }

  /** Zeigt den Übermittlungsbereich mit Hinweis zu ggf. noch zu erfassenden Daten an */
  renderUebermittlung() {
    if (!this.eingabenVollstaendig) {
      return (
        <Callout bsStyle="danger">
          <h4>Daten unvollständig</h4>
          <div>Es wurden noch nicht alle erforderlichen Daten gewählt ausgefüllt:
            <ul>
            {this.gewaehltePositionen.length === 0 && <li>Rechnungspositionen</li>}
            {this.rechnungVersandArt === 'digital' && this.mailRechnung.trim() === '' && <li>E-Mail Adresse für digitalen Rechnungsempfang</li>}
            </ul>
          </div>
        </Callout>
      );
    }

    return (
        <Callout bsStyle="warning">
          <h4>Auswahl bestätigen</h4>
          <p>Übermittelt die ausgewählten Positionen. Der Vorgang wird dadurch abgeschlossen, <b>eine nachträgliche Korrektur ist nicht mehr möglich!</b></p>
          <div>
            {this.dokumentStatus === 'nichtVorhanden' && (
              <div className="text-right">
                <Button bsStyle="warning" onClick={this.rechnungErzeugen} disabled={this.gewaehltePositionen.length === 0}>Bestätigen</Button>
              </div>)
            }
            {this.dokumentStatus === 'angefordert' && <p>Die Daten werden übermittelt...</p>}
          </div>
        </Callout>
    );
  }

  render() {
    return (
      <div className="maxwidth">
        <Callout bsStyle="success">
        <p>Bitte wählen Sie hier die Dienstleistungen aus die Sie in diesem Auftrag erbracht haben.</p>
          <p>Nach abschliessender Prüfung des Auftrags erstellt die DIQ Zert GmbH dann:</p>
          <ul>
            <li>Eine Rechnung die an den Auftraggeber übermittelt wird</li>
            <li>Eine Gutschrift die an Sie übermittelt wird</li>
          </ul>
          <p>Es gelten dabei die <a href="https://diq-zert.de/de/agb/">Allgemeinen Geschäftsbedingungen der DIQ Zert GmbH (AGB)</a>.</p>
        </Callout>

        <DienstleistungsUebersicht auftrag={this.auftragStore.auftrag} />
        <h3>Rechnungsversand</h3>
        <p>Hier können Sie angeben, wie die Rechnung durch die DIQ Zert an den Auftraggeber übermittelt werden soll. Der digtiale Rechnungsversand ist zu bevorzugen.</p>
        <Form horizontal={true}>
          <FormGroup>
            <Col componentClass={ControlLabel} sm={3}>Rechnungsversand</Col>
            <Col sm={6}>
              <FormControl componentClass="select" placeholder="select" value={this.rechnungVersandArt} onChange={this.handleRechnungVersandArtChanged}>
                <option value="digital"> Digital (per Mail)</option>
                <option value="post">Per Post (an Adress des Auftraggebers)</option>
              </FormControl>
            </Col>
          </FormGroup>
          {this.rechnungVersandArt === 'digital' &&
            <FormGroup validationState={this.mailRechnung === '' ? 'error' : undefined}>
              <Col componentClass={ControlLabel} sm={3}>E-Mail Empfänger</Col>
              <Col sm={6}>
                <InputGroup>
                  <FormControl type="text" label="rechnungmail" value={this.mailRechnung || ''} onChange={this.handleMailRechnungChanged} />
                  <OverlayTrigger trigger={['hover', 'focus']} placement="bottom" overlay={<Popover id="popover-trigger-hover-focus">Übernimmt die E-Mail aus der Adresse des Auftraggebers</Popover>}>
                    <InputGroup.Button>
                      <Button bsStyle="default" className="btn-group" onClick={this.handleMailRechnungAusAuftraggeber}>
                        <FaIcon icon="files-o" />
                      </Button>
                    </InputGroup.Button>
                  </OverlayTrigger>
                </InputGroup>
              </Col>
            </FormGroup>}
        </Form>


        <h3>Auswahl der Dienstleistungen</h3>

        {!this.vorhandenePositionen ?
          <div>lade Positionen...</div> :
          <div className="dienstleistungsnachweis">
            <Table striped={true} bordered={false} condensed={true} hover={true} className="dienstleistungsnachweis-eingabe">
              <thead>
                <tr>
                  <th>Position</th>
                  <th className="text-center">Anzahl</th>
                  <th className="text-right">Preis</th>
                </tr>
              </thead>
              <tfoot>
                <tr>
                  <td />
                  <td className="text-center"><b>Summe</b></td>
                  <td className="text-right"><b>{this.gewaehltePositionen.reduce((a, b) => a + b.preis, 0).toFixed(2).replace('.', ',')} €</b></td>
                </tr>
              </tfoot>
              <tbody>
                {this.vorhandenePositionen.map((position, index) =>
                  <Rechnungsposition
                    key={index}
                    position={position}
                    anzahl={this.gewaehltePositionen.filter((filterposition) => (filterposition.bezeichnung === position.bezeichnung)).length}
                    setAnzahlHandler={this.setAnzahlHandler}
                    preiseAnzeigen={true}
                  />
                )}
              </tbody>
            </Table>
          </div>
        }

        {this.renderUebermittlung()}

      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/abrechnung/Dienstleistungsnachweis.tsx